// Badges/chips compartilhados entre StudyWorkspace e ProvaWorkspace.
// Mostram metadados da questão (#numero_original, tema, fonte, dificuldade,
// caso, imagem, tabela, anulada). Esses chips NÃO carregam feedback de
// acerto/erro — ficam idênticos em estudo e prova.

import {
  AlignLeft,
  Image as ImageIcon,
  Table as TableIcon,
  Tag
} from "lucide-react";
import type { QuestaoStudy } from "./api";

// Classe-base de todos os chips: altura fixa (h-6), rounded, espaçamento.
export const CHIP_BASE =
  "inline-flex h-6 items-center rounded px-2 leading-none whitespace-nowrap";

export function questaoHasImages(q: QuestaoStudy): boolean {
  if (typeof q.imagem_url === "string" && q.imagem_url) return true;
  return Array.isArray(q.imagens) && q.imagens.length > 0;
}

export function questaoHasTabelas(q: QuestaoStudy): boolean {
  return Array.isArray(q.tabelas) && q.tabelas.length > 0;
}

// Estrelas de dificuldade (1-5). SVG inline pra garantir fill consistente
// independente de como o Lucide propaga `fill` em alguns ambientes.
function StarSolid({ filled, size = 12 }: { filled: boolean; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? "#f59e0b" : "none"}
      stroke={filled ? "#f59e0b" : "currentColor"}
      stroke-width="2"
      stroke-linejoin="round"
      stroke-linecap="round"
      class={filled ? "" : "text-muted-foreground/50"}
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

export function DifficultyStars({ value }: { value: number }) {
  const n = Math.max(0, Math.min(5, Math.round(value)));
  return (
    <span title={`Dificuldade ${n}/5`} class={CHIP_BASE + " gap-0.5 bg-accent"}>
      {[0, 1, 2, 3, 4].map((i) => (
        <StarSolid key={i} filled={i < n} size={12} />
      ))}
    </span>
  );
}

// Conjunto completo de badges da questão. Props permitem ocultar tema/dif
// (modo prova com "ocultar tema/dificuldade").
export function QuestionBadges({
  q,
  anulada,
  ocultarTema,
  ocultarDificuldade
}: {
  q: QuestaoStudy;
  anulada?: boolean;
  ocultarTema?: boolean;
  ocultarDificuldade?: boolean;
}) {
  return (
    <div class="flex flex-wrap items-center gap-2 text-[11px]">
      {q.numero_original != null && (
        <span
          title="Número da questão no arquivo original"
          class={
            CHIP_BASE +
            " gap-0.5 bg-primary/15 font-mono font-bold text-primary ring-1 ring-primary/30"
          }
        >
          #{String(q.numero_original)}
        </span>
      )}
      {!ocultarTema && q.tema && (
        <span class={CHIP_BASE + " gap-1.5 bg-primary/10 font-medium text-primary"}>
          <Tag width="1.3em" height="1.3em" strokeWidth={2.2} />
          {q.tema}
        </span>
      )}
      {(q.fonte || q.arquivo_origem) && (
        <span
          title="Arquivo/banco de origem"
          class={CHIP_BASE + " bg-accent/70 text-[10px] text-muted-foreground"}
        >
          {q.fonte || q.arquivo_origem}
        </span>
      )}
      {!ocultarDificuldade && q.dificuldade != null && q.dificuldade >= 1 && (
        <DifficultyStars value={Number(q.dificuldade)} />
      )}
      {q.caso && (
        <span
          title="Contém situação (texto/caso clínico que antecede a pergunta)"
          class={CHIP_BASE + " gap-1 bg-sky-500/15 font-medium text-sky-600 dark:text-sky-400"}
        >
          <AlignLeft width="1.3em" height="1.3em" /> Situação
        </span>
      )}
      {questaoHasImages(q) && (
        <span
          title="Contém imagem"
          class={CHIP_BASE + " gap-1 bg-violet-500/15 font-medium text-violet-600 dark:text-violet-400"}
        >
          <ImageIcon width="1.3em" height="1.3em" /> Imagem
        </span>
      )}
      {questaoHasTabelas(q) && (
        <span
          title="Contém tabela"
          class={CHIP_BASE + " gap-1 bg-teal-500/15 font-medium text-teal-600 dark:text-teal-400"}
        >
          <TableIcon width="1.3em" height="1.3em" /> Tabela
        </span>
      )}
      {anulada && (
        <span class={CHIP_BASE + " bg-amber-500/20 font-medium text-amber-600 dark:text-amber-400"}>
          Anulada
        </span>
      )}
    </div>
  );
}
